import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PropertyRules from "../../components/ListProperties/PropertyRules";
import { useMilestone } from "../../contexts/MilestoneContext";
import { Amenity } from "../../models/amenity.model";
import { Property } from "../../models/property.model";
import { amenityService } from "../../service/amenity.service";
import { bookingService } from "../../service/bookings.service";
import { complementaryService } from "../../service/complementary.service";
import { propertyService } from "../../service/property.service";
import BookingMilestone from "../BookingMilestone/BookingMilestone";
import Footer from "../Footer";
import PropertyAmenities from "../ListProperties/PropertyAmenities";
import PageHeader from "../PageHeader";
import RoomGallery from "../Room/RoomGallery";

const ReservePage: React.FC = () => {
  const toast = useRef<Toast>(null);
  const formRef = useRef<HTMLDivElement | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentStep } = useMilestone();

  // Set step to 3 when on reserve page (Request Reservation)
  useEffect(() => {
    setCurrentStep(3);
  }, [setCurrentStep]);

  const { propertyId, roomId, roomName, checkIn, checkOut, roomImages } =
    location.state || {};
  const normalizedRoomImages: any[] = React.useMemo(() => {
    const raw = roomImages?.$values ?? roomImages ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [roomImages]);
  const [propertyDetails, setPropertyDetails] = useState<Property | null>(null);
  const [facilities, setFacilities] = useState<any[]>([]);
  const [complementaries, setComplementaries] = useState<any[]>([]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    noOfAdults: 1,
    noOfChildrens: 0,
  });
  const [inputErrors, setInputErrors] = useState<{
    [key: string]: boolean | string;
  }>({});
  const [amenityModalVisible, setAmenityModalVisible] = useState(false);
  const [groupedAmenities, setGroupedAmenities] = useState<
    { parent: Amenity; children: Amenity[] }[]
  >([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const displayedItems = complementaries.slice(0, 4);
  const [showAll, setShowAll] = useState(false);
  const isFormValid = !!(
    form.firstName &&
    form.lastName &&
    form.mobile &&
    form.email &&
    form.noOfAdults > 0
  );
  const getLengthOfStay = () => {
    if (!checkIn || !checkOut) return "";
    const diffDays = Math.round(
      (new Date(checkOut).getTime() - new Date(checkIn).getTime()) /
        (1000 * 60 * 60 * 24)
    );
    return diffDays === 1 ? "1 night" : `${diffDays} nights`;
  };
  const formatDateTime = (dateString: string) =>
    new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  const handleBookNow = async () => {
    const errors: { [key: string]: boolean | string } = {};
    if (!form.firstName) errors.firstName = "First Name is required.";
    if (!form.lastName) errors.lastName = "Last Name is required.";
    if (!form.mobile) errors.mobile = "Mobile number is required.";
    else if (!/^[6-9]\d{9,}$/.test(form.mobile))
      errors.mobile = "Enter a valid 10-digit mobile number.";
    if (!form.email) errors.email = "Email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errors.email = "Enter a valid email address.";
    if (form.noOfAdults < 1)
      errors.noOfAdults = "At least 1 adult is required.";
    if (form.noOfChildrens < 0)
      errors.noOfChildrens = "Number of children cannot be negative.";
    setInputErrors(errors);

    if (Object.keys(errors).length > 0) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      // toast.current?.show({ severity: 'warn', summary: 'Missing Fields', detail: 'Please fill all required fields.', life: 3000 });
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Please fill all required fields.",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      mobileNumber: Number(form.mobile),
      emailAddress: form.email,
      propertyId: Number(propertyId),
      checkIn,
      checkOut,
      noOfDays: Number(getLengthOfStay().split(" ")[0]) || 1,
      noOfAdults: form.noOfAdults,
      noOfChildrens: form.noOfChildrens,
      bookedRooms: roomId
        ? [
            {
              roomId: Number(roomId),
            },
          ]
        : [],
    };

    try {
      await bookingService.create(payload);
      // Navigate to thank you page after successful booking
      navigate("/reservation-thank-you");
    } catch (error: any) {
      console.error("Booking error:", error);

      // Handle specific error messages from backend
      let errorMessage = "Booking failed. Please try again.";

      if (error?.response?.data) {
        // Check for duplicate booking message
        if (error.response.data.includes("already booked")) {
          errorMessage =
            "This room is already booked. Please select a different room.";
        }
        // Use backend message if available
        else if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        }
        // Handle structured error response
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: errorMessage,
        showConfirmButton: false,
        timer: 4000,
      });
    }
  };

  // Helper function to render stars based on rating
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="pi pi-star-fill text-warning"></i>);
    }
    if (halfStar) {
      stars.push(<i key="half" className="pi pi-star-half text-warning"></i>);
    }
    while (stars.length < 5) {
      stars.push(
        <i
          key={`empty-${stars.length}`}
          className="pi pi-star text-warning"
        ></i>
      );
    }
    return stars;
  };

  // Fetch and group amenities
  useEffect(() => {
    if (!propertyId) return;

    setLoadingAmenities(true);

    Promise.all([
      amenityService.getAll(),
      amenityService.getAmenitiesByPropertyId(propertyId),
    ])
      .then(([masterRes, mappedRes]) => {
        const masterList: Amenity[] = Array.isArray(masterRes)
          ? masterRes
          : masterRes && Array.isArray((masterRes as any).$values)
          ? (masterRes as any).$values
          : [];

        const mappedList = Array.isArray(mappedRes) ? mappedRes : [];

        const mappedIds = mappedList.map((m: any) => m.amenityId);

        const mappedChildren = masterList.filter(
          (a) => mappedIds.includes(a.id) && a.parentAmenityId !== null
        );

        const parentIds = Array.from(
          new Set(mappedChildren.map((a) => a.parentAmenityId!))
        );
        const parents = masterList.filter((a) => parentIds.includes(a.id));

        const grouped = parents.map((parent) => ({
          parent,
          children: mappedChildren.filter(
            (child) => child.parentAmenityId === parent.id
          ),
        }));

        setGroupedAmenities(grouped);
      })
      .catch((err) => {
        console.error("Error loading amenities:", err);
        setGroupedAmenities([]);
      })
      .finally(() => setLoadingAmenities(false));
  }, [propertyId]);

  useEffect(() => {
    if (propertyId)
      propertyService.getById(propertyId, true).then(setPropertyDetails);
    if (roomId) {
      // facilityService.getFacilitiesByRoomId(roomId)
      //   .then(setFacilities)
      //   .catch((err) => {
      //     if (err?.response?.status === 404) setFacilities([]);
      //   });
      complementaryService
        .getByRoomId(roomId)
        .then(setComplementaries)
        .catch((err) => {
          if (err?.response?.status === 404) setComplementaries([]);
        });
    }
    window.scrollTo(0, 0);
  }, [propertyId, roomId]);

  return (
    <>
      <BookingMilestone />
      <div className="content has-nobreadcrumbs reserve-page">
        <PageHeader title="Reserve Room" showBackButton={false} />
        <div className="content-body">
          <div className="container mb-4">
            {/* Header Section */}
            <div className="bg-white p-4 rounded-4 shadow-sm">
              <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                <div className="property-summary flex-grow-1">
                  {/* Details Section */}
                  <div className="d-flex gap-2 justify-content-between flex-row">
                    <div className="d-flex gap-2 flex-column w-100">
                      <div className="meta-tag d-flex align-items-center gap-2 flex-wrap">
                        <span className="rating-badge">
                          {propertyDetails?.rating?.toFixed(1) || "9.2"}
                        </span>
                        <span className="rating-text">Exceptional</span>
                        <span className="text-muted ratings-count">
                          600+ Ratings
                        </span>
                        <span className="text-muted star-group">
                          {renderStars(propertyDetails?.rating || 4)}
                        </span>
                      </div>

                      <div className="d-flex align-items-start align-items-md-center justify-content-between flex-column flex-md-row gap-2">
                        <h2 className="property-title mb-0">
                          {propertyDetails?.name}
                        </h2>
                      </div>

                      {/* Meta line: property type • zone · city */}
                      <div className="property-meta">
                        {propertyDetails?.propertyType?.name && (
                          <span className="type">
                            {propertyDetails.propertyType.name}
                          </span>
                        )}
                        {(propertyDetails?.location?.zone?.name ||
                          propertyDetails?.city?.name) && (
                          <>
                            {propertyDetails?.propertyType?.name && (
                              <span className="dot">•</span>
                            )}
                            <span className="area">
                              {propertyDetails?.location?.zone?.name}
                              {propertyDetails?.city?.name && (
                                <>
                                  <span className="separator"> · </span>
                                  {propertyDetails.city.name}
                                </>
                              )}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Room types summary */}
                      {Array.isArray(propertyDetails?.roomTypes) &&
                        propertyDetails!.roomTypes!.length > 0 && (
                          <ul className="property-rooms list-unstyled mb-0">
                            {propertyDetails!
                              .roomTypes!.slice(0, 4)
                              .map((rt) => (
                                <li key={rt.id} className="room-pill">
                                  <i className="bi bi-house-door me-2" />
                                  {rt.name}
                                </li>
                              ))}
                            {propertyDetails!.roomTypes!.length > 4 && (
                              <li className="room-pill more">
                                +{propertyDetails!.roomTypes!.length - 4} more
                              </li>
                            )}
                          </ul>
                        )}

                      {/* About */}
                      {propertyDetails?.description && (
                        <div className="about-property mt-3">
                          <h3 className="about-title">
                            About {propertyDetails?.name}
                          </h3>
                          <p className="about-text mb-0">
                            {propertyDetails.description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Grouped Amenities */}
                  <div className="row text-start text-muted fs-7 mt-4 mb-4">
                    {loadingAmenities ? (
                      <div className="col-12">
                        <p className="text-muted">Loading amenities...</p>
                      </div>
                    ) : groupedAmenities.length > 0 ? (
                      groupedAmenities.slice(0, 3).map((group, index) => (
                        <div className="col-md-4 mb-4" key={index}>
                          <h6 className="fw-semibold text-dark mb-2 fs-6">
                            {group.parent.name}
                          </h6>
                          <ul className="list-unstyled d-flex flex-column gap-2 ps-0 mb-0">
                            {group.children.slice(0, 3).map((child) => (
                              <li
                                key={child.id}
                                className="d-flex align-items-center"
                              >
                                <i className="bi bi-check-circle-fill text-success me-2 fs-6"></i>
                                <span className="text-body">{child.name}</span>
                              </li>
                            ))}
                            {group.children.length > 3 && (
                              <li
                                className="text-primary link-pointer"
                                onClick={() => setAmenityModalVisible(true)}
                              >
                                +{group.children.length - 3} more
                              </li>
                            )}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <div className="col-12">
                        <p className="text-muted">
                          No amenities available for this property.
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Show All Amenities Button */}
                  {groupedAmenities.some(
                    (group) => group.children.length > 2
                  ) && (
                    <div className="mt-2">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setAmenityModalVisible(true)}
                      >
                        Show All Amenities
                      </button>
                    </div>
                  )}
                  {/* Amenities Modal */}
                  <Dialog
                    header="Property Amenities"
                    visible={amenityModalVisible}
                    style={{ width: "60vw", maxWidth: 1000 }}
                    onHide={() => setAmenityModalVisible(false)}
                    draggable={false}
                    resizable={false}
                    modal
                  >
                    {propertyDetails?.id && (
                      <PropertyAmenities propertyId={propertyDetails.id} />
                    )}
                  </Dialog>
                </div>
                <div className="property-gallery">
                  <RoomGallery images={normalizedRoomImages} />
                </div>
              </div>
            </div>
          </div>

          <div className="container d-flex flex-column gap-2">
            {/* Form & Side Cards */}
            <div className="row g-4">
              <div className="col-lg-8">
                <div ref={formRef} className="bg-white p-4 rounded-4 shadow-sm">
                  <h4 className="fw-bold mb-3 border-bottom pb-2">
                    Your Details
                  </h4>
                  {(["firstName", "lastName", "mobile", "email"] as const).map(
                    (field) => (
                      <div className="mb-3" key={field}>
                        <label className="form-label fw-semibold text-capitalize">
                          {field.replace("Name", " Name")}{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <InputText
                          name={field}
                          value={form[field] as string}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              [field]:
                                field === "mobile"
                                  ? e.target.value.replace(/\D/g, "")
                                  : e.target.value,
                            }))
                          }
                          className="w-100"
                          placeholder={
                            field === "email" ? "you@example.com" : ""
                          }
                        />
                        {inputErrors[field] && (
                          <small className="text-danger mt-1 d-block">
                            {typeof inputErrors[field] === "string"
                              ? inputErrors[field]
                              : `${field} is required.`}
                          </small>
                        )}
                      </div>
                    )
                  )}

                  {/* Number of Adults */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Number of Adults <span className="text-danger">*</span>
                    </label>
                    <InputText
                      name="noOfAdults"
                      type="number"
                      min="1"
                      value={form.noOfAdults.toString()}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          noOfAdults: Math.max(
                            1,
                            parseInt(e.target.value) || 1
                          ),
                        }))
                      }
                      className="w-100"
                      placeholder="Number of adults"
                    />
                    {inputErrors.noOfAdults && (
                      <small className="text-danger mt-1 d-block">
                        {typeof inputErrors.noOfAdults === "string"
                          ? inputErrors.noOfAdults
                          : "Number of adults is required."}
                      </small>
                    )}
                  </div>

                  {/* Number of Children */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Number of Children
                    </label>
                    <InputText
                      name="noOfChildrens"
                      type="number"
                      min="0"
                      value={form.noOfChildrens.toString()}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          noOfChildrens: Math.max(
                            0,
                            parseInt(e.target.value) || 0
                          ),
                        }))
                      }
                      className="w-100"
                      placeholder="Number of children"
                    />
                    {inputErrors.noOfChildrens && (
                      <small className="text-danger mt-1 d-block">
                        {typeof inputErrors.noOfChildrens === "string"
                          ? inputErrors.noOfChildrens
                          : "Number of children must be 0 or more."}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-4 d-flex flex-column gap-2">
                {/* Complementaries */}
                <div className="bg-white p-4 rounded-4 shadow-sm">
                  <h5 className="fw-bold text border-bottom pb-2 mb-3">
                    Complementaries
                  </h5>

                  <ul className="list-unstyled mb-0">
                    {complementaries.length > 0 ? (
                      <>
                        {displayedItems.map((item, idx) => (
                          <li
                            key={item.id || idx}
                            className="mb-2 d-flex align-items-center"
                          >
                            <i className="bi bi-gift text-success me-2" />
                            {item.name}
                          </li>
                        ))}

                        {complementaries.length > 4 && (
                          <li className="mt-2">
                            <button
                              className="btn btn-sm btn-link p-0"
                              onClick={() => setShowAll(true)}
                            >
                              Show All
                            </button>
                          </li>
                        )}
                      </>
                    ) : (
                      <li className="text-muted">No complementaries listed</li>
                    )}
                  </ul>

                  <Dialog
                    header="All Complementaries"
                    visible={showAll}
                    onHide={() => setShowAll(false)}
                    style={{ width: "60vw" }}
                    breakpoints={{ "768px": "90vw" }}
                    modal
                  >
                    <div className="container-fluid">
                      <div className="row">
                        {complementaries.map((item, idx) => (
                          <div
                            key={item.id || idx}
                            className="col-md-4 mb-3 d-flex align-items-center"
                          >
                            <i className="bi bi-gift text-success me-2" />
                            {item.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Dialog>
                </div>

                {/* Facilities */}
                {/* <div className="bg-white p-4 rounded-4 shadow-sm">
                <h5 className="fw-bold text border-bottom pb-2 mb-3">Facilities</h5>
                <ul className="list-unstyled mb-0">
                  {facilities.length > 0 ? (
                    facilities.map((item, idx) => (
                      <li key={item.id || idx} className="mb-2 d-flex align-items-center">
                        <i className="pi pi-check-circle text-success me-2" />
                        {item.name}
                      </li>
                    ))
                  ) : (
                    <li className="text-muted">No facilities listed</li>
                  )}
                </ul>
              </div> */}
              </div>
            </div>
            {/* <div className="bg-white p-4 rounded-4 shadow-sm"> */}
            {/* Booking Summary */}
            <div className="bg-white rounded-4 p-4 shadow-sm mb-4 border">
              <h5 className="fw-bold text border-bottom pb-2 mb-3">
                Booking Summary
              </h5>
              <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                <li>
                  <b>Check-in:</b>{" "}
                  <span className="ms-2">{formatDateTime(checkIn)}</span>
                </li>
                <li>
                  <b>Check-out:</b>{" "}
                  <span className="ms-2">{formatDateTime(checkOut)}</span>
                </li>
                <li>
                  <b>Length of stay:</b>{" "}
                  <span className="ms-2">{getLengthOfStay()}</span>
                </li>
              </ul>
              {/* </div> */}
            </div>
            {/* Terms & Conditions */}
            <div className="bg-white rounded-4 p-4 shadow-sm mb-4 border">
              <h5 className="fw-bold text border-bottom pb-2 mb-0">
                Terms & Conditions
              </h5>
              <PropertyRules property={propertyDetails} />
            </div>

            {/* Footer Section */}
            <div className="bg-white p-4 rounded-4 shadow-sm">
              {/* Cancellation Policies */}
              <h5 className="fw-bold fs-5 mb-3">Cancellation Policies</h5>
              {[
                {
                  title: "Cancellation & Modifications",
                  items: [
                    "Free Cancellation until 2:00 PM on May 22",
                    "Late cancellations may not be accepted.",
                    "Changes after the deadline must be reviewed.",
                    "Authorized requests only.",
                    "Special rates may be non-refundable.",
                  ],
                },
                {
                  title: "Communication & Guest Responsibilities",
                  items: [
                    "Notify property of changes promptly.",
                    "Respect property rules and staff.",
                    "Use correct contact details.",
                    "Inform of late arrival or early exit.",
                    "Behave responsibly during your stay.",
                  ],
                },
              ].map((section, idx) => (
                <details
                  key={idx}
                  className="mb-3 border rounded-3 p-3 bg-light"
                >
                  <summary className="fw-bold fs-6 cursor-pointer">
                    {section.title}
                  </summary>
                  <ul className="mt-2 ms-4">
                    {section.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </details>
              ))}

              <p className="text-muted mt-4 mb-4 note-14">
                By clicking <strong>"Book Now,"</strong> you confirm that you've
                read and accepted the cancellation policies and terms &
                conditions above.
              </p>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-start flex-column flex-md-row gap-4">
                {/* Left box with secure booking info */}
                <div className="bg-success-subtle rounded-4 p-4 shadow-sm w-50">
                  <div className="fw-semibold mb-2">
                    <i className="pi pi-check text-success me-2"></i> Your
                    booking is secure.
                  </div>
                  <div className="fw-semibold mb-2">
                    <i className="pi pi-check text-success me-2"></i> Free
                    cancellation available.
                  </div>
                  <div className="fw-semibold">
                    <i className="pi pi-check text-success me-2"></i>{" "}
                    Confirmation email sent instantly.
                  </div>
                </div>

                {/* Right side buttons */}
                <div className="d-flex flex-column align-items-end gap-2">
                  <div className="btn-fixed-180">
                    <Button
                      type="button"
                      label="Go Back"
                      className="p-button-secondary"
                      icon="pi pi-arrow-left"
                      onClick={() => navigate(-1)}
                      style={{
                        width: "100%",
                        height: 48,
                        fontWeight: 700,
                        fontSize: 18,
                        borderRadius: 8,
                        border: "none",
                      }}
                    />
                  </div>
                  <div className="btn-fixed-180">
                    <Button
                      type="button"
                      label="Book now"
                      className="p-button-dark"
                      style={{
                        width: "100%",
                        height: 48,
                        fontWeight: 700,
                        fontSize: 18,
                        background: isFormValid ? "#27ae60" : "#000",
                        border: "none",
                        borderRadius: 8,
                      }}
                      onClick={handleBookNow}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
          <Toast ref={toast} />
        </div>
      </div>
    </>
  );
};

export default ReservePage;
